import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_HINTS = {
  Piece_identite: 'CNI ou passeport, recto-verso, en cours de validite, pour chaque investisseur',
  Bulletins_salaire: 'Les 3 derniers bulletins de chaque investisseur (PDF ou photo lisible)',
  Avis_imposition: 'Les derniers avis d\'imposition, toutes pages',
  Releves_bancaires: '3 derniers releves de chaque compte (courant individuel + joint)',
  Tableau_amortissement: 'Un depot par credit en cours : choisissez le type de credit puis envoyez le tableau correspondant',
  Baux: 'Un depot par bail en cours : indiquez le bien concerne puis envoyez le bail correspondant',
  Justificatifs_epargne: 'Livret A, LDDS, PEL, assurance-vie, PEA - dernier releve de chaque (plusieurs fichiers d\'un coup possibles)',
  Justificatif_domicile: 'Facture energie, internet ou quittance de loyer de moins de 3 mois',
};

function DocumentSlot({ doc, token, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const [error, setError] = useState('');
  const [docLabel, setDocLabel] = useState('');
  const inputRef = useRef(null);
  const alreadyReceived = !!doc.already_uploaded;
  const previousCount = doc.uploaded_count || 0;
  const labelField = doc.label_field || null;
  const labelMissing = !!(labelField && labelField.required && !docLabel.trim());

  const handleSelect = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    setFiles(list);
    setError('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const list = Array.from(e.dataTransfer.files || []);
    if (list.length === 0) return;
    setFiles(list);
    setError('');
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (labelMissing) {
      setError(`Merci de renseigner « ${labelField.name} » avant d'envoyer.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('document_type', doc.key);
      if (docLabel.trim()) fd.append('doc_label', docLabel.trim());
      files.forEach(f => fd.append('file', f));
      const r = await axios.post(`${API}/drive/public/upload/${token}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploaded(prev => [...prev, ...(r.data.files || [])]);
      setFiles([]);
      setDocLabel('');
      if (inputRef.current) inputRef.current.value = '';
      onUploaded && onUploaded();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors du depot');
    } finally {
      setUploading(false);
    }
  };

  const totalUp = uploaded.length + previousCount;

  return (
    <div className={`rounded-xl border-2 p-5 transition-colors ${alreadyReceived ? 'border-emerald-300 bg-emerald-50/40' : (totalUp > 0 ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 bg-white hover:border-[#008A8D]')}`} data-testid={`doc-slot-${doc.key}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            {doc.label}
            {!doc.required && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                Optionnel
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">{DOCUMENT_HINTS[doc.key] || ''}</p>
          {alreadyReceived && uploaded.length === 0 && (
            <p className="text-xs text-emerald-700 mt-1 font-medium" data-testid={`already-received-${doc.key}`}>
              <svg className="w-3 h-3 inline-block mr-0.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
              Deja recu lors de votre pre-qualification ({previousCount} fichier{previousCount > 1 ? 's' : ''}). Vous pouvez en ajouter un complement si besoin.
            </p>
          )}
        </div>
        {totalUp > 0 && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
            {totalUp} depose(s)
          </span>
        )}
      </div>

      {labelField && (
        <div className="mb-3" data-testid={`doc-label-field-${doc.key}`}>
          <label className="block text-xs font-bold text-gray-700 mb-1">
            {labelField.name} {labelField.required && <span className="text-red-500">*</span>}
          </label>
          {labelField.type === 'select' ? (
            <select
              value={docLabel}
              onChange={(e) => { setDocLabel(e.target.value); setError(''); }}
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#008A8D] focus:outline-none bg-white"
              data-testid={`doc-label-select-${doc.key}`}
            >
              <option value="">— Choisissez le type de credit —</option>
              {(labelField.choices || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={docLabel}
              onChange={(e) => { setDocLabel(e.target.value); setError(''); }}
              placeholder={labelField.placeholder || ''}
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#008A8D] focus:outline-none"
              data-testid={`doc-label-input-${doc.key}`}
            />
          )}
          {labelField.hint && <p className="text-[11px] text-gray-400 mt-1">{labelField.hint}</p>}
        </div>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 hover:border-[#008A8D]"
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={handleSelect}
          data-testid={`doc-input-${doc.key}`}
        />
        <svg className="w-8 h-8 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
        <p className="text-sm text-gray-600">Cliquez ou glissez-deposez vos fichiers ici</p>
        <p className="text-xs text-gray-400 mt-0.5">PDF, JPG, PNG acceptes - plusieurs fichiers possibles</p>
      </div>

      {files.length > 0 && (
        <div className="mt-3 space-y-1" data-testid={`doc-preview-${doc.key}`}>
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-700 bg-gray-50 rounded px-2 py-1">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <span className="truncate flex-1">{f.name}</span>
              <span className="text-gray-400">{Math.round(f.size / 1024)} ko</span>
            </div>
          ))}
          <button
            onClick={handleUpload}
            disabled={uploading || labelMissing}
            title={labelMissing ? `Renseignez d'abord « ${labelField.name} »` : ''}
            className="mt-2 w-full px-4 py-2 bg-[#008A8D] text-white rounded-lg font-semibold text-sm hover:bg-[#006B6D] disabled:opacity-50"
            data-testid={`doc-submit-${doc.key}`}
          >
            {uploading ? 'Envoi en cours...' : (labelMissing ? `Renseignez « ${labelField.name} » pour envoyer` : `Envoyer ${files.length} fichier(s)`)}
          </button>
        </div>
      )}

      {uploaded.length > 0 && (
        <div className="mt-3 space-y-1">
          {uploaded.map((u) => (
            <div key={u.file_id} className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
              <span className="truncate flex-1">{u.doc_label ? `${u.doc_label} — ` : ''}{u.filename}</span>
            </div>
          ))}
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default function PublicUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchInfo = () => {
    axios.get(`${API}/drive/public/upload/${token}`)
      .then(r => { setInfo(r.data); setLoading(false); })
      .catch(e => {
        setError(e.response?.data?.detail || 'Lien invalide');
        setLoading(false);
      });
  };

  useEffect(() => { fetchInfo(); /* eslint-disable-next-line */ }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-500 mt-3">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md bg-white rounded-xl shadow-lg p-8 text-center" data-testid="upload-error">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide ou expire</h1>
          <p className="text-gray-600 text-sm">{error}</p>
          <p className="text-gray-500 text-xs mt-4">Contactez votre conseiller RealEase pour recevoir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4" data-testid="public-upload-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-gradient-to-r from-[#008A8D] to-[#006B6D] rounded-2xl p-8 text-white mb-8 shadow-lg">
          <h1 className="text-2xl sm:text-3xl font-bold">Depot securise de vos pieces</h1>
          <p className="mt-2 text-white/90">
            Bonjour {info.client_name}, deposez les documents demandes pour l'analyse de votre dossier par notre courtier partenaire.
          </p>
          <p className="mt-3 text-xs text-white/70">
            Lien securise et confidentiel - {info.uploaded_count > 0 ? `${info.uploaded_count} document(s) deja deposes` : 'Aucun document depose pour le moment'} - Expire le {new Date(info.expires_at).toLocaleDateString('fr-FR')}
          </p>
        </div>

        <div className="space-y-4">
          {info.expected_documents.map(d => (
            <DocumentSlot key={d.key} doc={d} token={token} onUploaded={fetchInfo} />
          ))}
        </div>

        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
          <p className="font-semibold mb-1">Confidentialite garantie</p>
          <p className="text-amber-800">Vos documents sont stockes dans votre dossier client securise. Seule l'equipe RealEase et le courtier partenaire (sous engagement de confidentialite) y ont acces.</p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">RealEase - Accompagnement en investissement immobilier rentable</p>
      </div>
    </div>
  );
}
